/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useRef, useState } from "react";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { IconButton, Box, alpha, useTheme, Tooltip } from "@mui/material";
import { useCall } from "../../provider/CallProvider";

const MicLevel: React.FC = () => {
  const theme = useTheme();
  const [displayLevel, setDisplayLevel] = useState(0);
  const callContext = useCall();
  const smoothRef = useRef(0);

  // simple one-pole smoothing
  const smooth = (x: number) => {
    const prev = smoothRef.current;
    const next = x > prev ? 0.4 * x + 0.6 * prev : 0.9 * x + 0.1 * prev;
    smoothRef.current = next;
    return next;
  };

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let cancelled = false;

    const { device, call, muted } = callContext;

    if (call && call.status() !== "closed") {
      const onVolume = (inputVolume: number, _outputVolume: number) => {
        const v = muted ? 0 : inputVolume;
        setDisplayLevel(smooth(v));
      };
      const onMute = (isMuted: boolean) => {
        if (isMuted) {
          smoothRef.current = 0;
          setDisplayLevel(0);
        }
      };

      call.on("volume", onVolume);
      call.on("mute", onMute);

      cleanup = () => {
        call.off("volume", onVolume);
        call.off("mute", onMute);
      };
    } else if (device && device.audio) {
      const audio = device.audio;

      const startInputMeter = async () => {
        try {
          if (!audio.inputDevice) {
            await audio.setInputDevice("default");
          }
        } catch (_) {
          // ignore: user may not have granted mic access yet
        }

        const onInputVolume = (vol: number) => {
          const v = callContext.muted ? 0 : vol;
          setDisplayLevel(smooth(v));
        };

        if (cancelled) {
          return;
        }

        audio.on("inputVolume", onInputVolume);
        cleanup = () => audio.off("inputVolume", onInputVolume);
      };

      startInputMeter();
    } else {
      smoothRef.current = 0;
      setDisplayLevel(0);
    }

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [callContext.device, callContext.call, callContext.muted]);

  const button = (
    <IconButton
      onClick={callContext.toggleMute}
      color={callContext.muted ? "error" : "primary"}
      disabled={!callContext.call && !callContext.device}
    >
      {callContext.muted ? <MicOffIcon fontSize="large" /> : <MicIcon fontSize="large" />}
    </IconButton>
  );

  return (
    <IconButton sx={{ mt: 1, position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          backgroundColor: alpha(theme.palette.primary.main, 0.35),
          transform: `scale(${1 + displayLevel * 0.4})`,
          opacity: displayLevel,
          transition: "transform 80ms linear, opacity 80ms linear",
        }}
      />

      {callContext.call ? (
        <Tooltip title={callContext.muted ? "Stummschaltung aufheben" : "Mikrofon stummschalten"}>{button}</Tooltip>
      ) : (
        button
      )}
    </IconButton>
  );
};

export default MicLevel;
